import React from "react";
import { Check } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import MessageAttachments from "./MessageAttachments";
import MessageReactions from "./MessageReactions";
import linkify from "../../lib/linkify";
import PlusBadge from "./PlusBadge";

function safeTime(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

/**
 * DmBubble — single source of truth for a DM message bubble.
 * Used by both the artist (dark) and admin (light) DM views.
 *
 * Props:
 *  - msg: the message document
 *  - mine: am I the sender?
 *  - myReactorId: 'admin' for the admin view, user.id for the artist view
 *  - isLast: is this the LAST message I (the viewer) sent?
 *  - readByOther: did the receiver read my last own message? (mine && isLast)
 *  - selfLabel / otherLabel: rendered as "<label> · 2m ago"
 *  - onReact: async (messageId, emoji) => updatedMessage
 *  - theme: 'dark' | 'light'
 *  - seenTestId: optional data-testid for the Seen indicator
 */
export default function DmBubble({
  msg,
  mine,
  myReactorId,
  isLast,
  readByOther,
  selfLabel = "You",
  otherLabel,
  onReact,
  theme = "dark",
  seenTestId,
}) {
  const attachments = msg.attachments || [];
  const hasBody = msg.body && msg.body.trim();
  const showSeen = mine && isLast && readByOther;

  const dark = theme === "dark";
  const bubbleClass = mine
    ? "bg-paul-yellow text-navy rounded-br-sm"
    : dark
      ? "bg-[#1b2138] text-white border border-white/5 rounded-bl-sm"
      : "bg-gray-100 text-navy rounded-bl-sm";
  const padding = dark ? "px-4 py-3" : "px-4 py-2.5";
  const captionColor = dark ? "text-white/40" : "text-gray-400";
  const seenColor = dark ? "text-paul-yellow/80" : "text-paul-yellow";
  const senderLabel = mine
    ? selfLabel
    : otherLabel || (msg.senderRole === "admin" ? "Paul" : msg.senderName);

  return (
    <div className={`group flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[78%] md:max-w-[60%] w-full md:w-auto">
        {hasBody && (
          <div
            data-testid={`dm-bubble-body-${msg.id}`}
            className={`rounded-2xl ${padding} text-sm leading-relaxed whitespace-pre-wrap break-words ${bubbleClass}`}
            style={mine ? { color: "#0b1535" } : undefined}
          >
            {linkify(msg.body, {
              className: mine
                ? "underline decoration-navy/40 hover:decoration-navy break-words"
                : dark
                  ? "underline decoration-paul-yellow/60 hover:decoration-paul-yellow text-paul-yellow break-words"
                  : "underline decoration-paul-yellow/60 hover:decoration-paul-yellow text-paul-yellow break-words",
            })}
          </div>
        )}
        {attachments.length > 0 && (
          <div className={hasBody ? "mt-2" : ""}>
            <MessageAttachments attachments={attachments} mine={mine} />
          </div>
        )}
        <MessageReactions
          reactions={msg.reactions || []}
          myReactorId={myReactorId}
          onToggle={(emoji) => onReact(msg.id, emoji)}
          align={mine ? "right" : "left"}
          theme={theme}
        />
        <div
          className={`text-[11px] mt-1 flex items-center gap-1 ${captionColor} ${mine ? "justify-end" : "justify-start"}`}
        >
          <span className="inline-flex items-center gap-1">
            {senderLabel} · {safeTime(msg.createdAt)}
            {!mine && msg.senderRole === "user" && <PlusBadge tier={msg.senderTier} size="xs" />}
          </span>
          {showSeen && (
            <span data-testid={seenTestId} className={`flex items-center ${seenColor}`}>
              <Check className="w-3 h-3 -mr-1.5" />
              <Check className="w-3 h-3" />
              <span className="ml-0.5">Seen</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
